define((require) => {
    "use strict";

    const $                          = require("jquery");
    const _                          = require("underscore");
    const Browser                    = require("shared/services/Browser");
    const ExperienceViewModel        = require("shared/ui/viewer/ExperienceViewModel");
    const InitialScenesController    = require("shared/ui/viewer/InitialScenesController");
    const {Logger}                     = require("shared/util/Logger");
    const Marionette                 = require("marionette");
    const Promise                    = require("shared/util/Promise");
    const ResponsiveViewerController = require("shared/ui/viewer/ResponsiveViewerController");
    const SceneNavigationAction      = require("shared/ui/experience/action/sceneNavigation/SceneNavigationAction");
    const SceneTransitionController  = require("shared/ui/viewer/SceneTransitionController");
    const SceneViewer                = require("shared/ui/viewer/SceneViewer");
    const template                   = require("shared/ui/viewer/ExperienceViewer.tpl");
    const Window                     = require("shared/services/Window");

    const Region = Marionette.Region;

    const CLASS_PREFIX = "scene region";
    const NUM_REGIONS = 3; /* Must be an odd number */

    const log = new Logger("ExperienceViewer");

    /**
     * Experience viewer.
     *
     * Used to view an experience with one or more scenes.
     *
     * @author Kim
     * @class ExperienceViewer
     *
     * @cfg {ExperienceViewModel} model The experience view model.
     */
    return Marionette.Layout.extend({ // Not ImprovedView, since this class is used in the viewer

        className: "experience-viewer",

        template,

        responsiveViewerControllerOptions: null,

        _autoPlayIntervalId: null,

        /**
         * Additional options passed to the ResponsiveViewerController.
         *
         * See <tt>PreviewExperienceViewer</tt>.
         *
         * @type {Object|Function}
         */
        _responsiveViewerController: null,

        _transitionController: null,

        _sceneRegions: null,

        _shownDeferred: null,

        _pause(paused) {
            // Avoid triggering needless events when stalled...
            if (!this.model.get("stalled")) {
                // Special edge case: if the user still hovers on this Experience at the time of unpause (at some
                // earlier point in time), "paused" may actually already be false but auto play will not have been
                // started because of the _.defer(..) wrapper to check for hover on next focus. We can thus be here
                // if we get a "mouseleave" event before the _.defer(..) callback fires. We thus have to start auto
                // play manually here in case unpaused already and about to unpause as well, since no change:paused
                // event will then fire...
                const pause = this.model.get("paused");
                this.model.set("paused", paused);
                if (!pause && !paused) {
                    this._startAutoPlay();
                }
            }
        },

        events: {
            "mouseenter": "_onMouseEnter",
            "mouseleave": "_onMouseLeave"
        },

        regions: {
            overlayRegion: ".overlay-region",
            backgroundRegion: ".background-region"
        },

        modelEvents: {
            "change:currentScene":       "_currentSceneChangeHandler",
            "change:viewportVisibility": "_visibilityChangeHandler"
        },

        initialize() {
            this._responsiveViewerController = new ResponsiveViewerController(_.extend({
                viewer:              this,
                viewportSize:        this.options.fixedSize,
                viewportInitialSize: this.options.initialSize,
                embedContainer:      this.options.embedContainer,
                verticalAlign:       this.options.verticalAlign,
                horizontalAlign:     this.options.horizontalAlign,
                scrollableContainer: this.options.scrollableContainer
            }, _.result(this, "responsiveViewerControllerOptions")));

            this._transitionController = new SceneTransitionController(this.model);
            this._shownDeferred = Promise.deferred();

            this.experience  = this.model.get("experience");
            this.numOfScenes = this.experience.getNumberOfScenes();

            // Listen for scale/resize changes in order to update the experience size
            this.listenTo(this.experience, "change:scale", this._scaleChangeHandler);

            this._currentSceneChangeHandler = _.throttle(this._currentSceneChangeHandler, 501); // magic number!? (from git commit sha: cdbdce23506)

            this._addEventListeners();
        },

        _onMouseEnter() {
            this._pause(true);
        },

        _onMouseLeave() {
            this._pause(false);
        },

        /**
         * Convenience method returning a promise that resolves when the view is initially
         * shown (i.e. triggers it's 'show' event).
         *
         * @return {Promise} a promise that resolves when the view is initially
         * shown (i.e. triggers it's 'show' event).
         */
        ready() {
            const deferred = Promise.deferred();

            this.listenToOnce(this, "show", deferred.resolve);

            return deferred.promise();
        },

        shown() {
            return this._shownDeferred.promise();
        },

        fadeIn() {
            if (this.$el) {
                this.$el.addClass("fade-in");
            }
        },

        /**
         * Sets the position of the experience viewer.
         *
         * @param  {Number} x The position on the x axis.
         * @param  {Number} y The position on the y axis.
         */
        position(x, y) {
            Browser.transform(this.el, {x, y});
        },

        _addEventListeners() {
            // Listen for tab changes in the browser, so we can differentiate between normal activation/deactivation
            // and user navigation via browser tabs, but always trigger a corresponding "change:active" event. We only
            // listen for this for non-embedded top mosyt Viewers as it will recursively triggered an "active" change
            // to any embedded children...
            this.listenTo(this.model, "change:shown", (model, shown, options) => {
                log.verbose(`Page ${shown ? "show" : "hide"} for: ${model.get("experience")}`);

                // Did the shown event originate from this - if so top most - Viewer, or via top down
                // recursion from a parent Viewer? If so, trigger "change:active" that will recurse
                // on its own, otherwise do not as any child will be notified via recursion. However,
                // we must manually recurse to notify children about "shown" event always, as embedded
                // Viewers will not listen for such an event. We have to do it like this to ensure
                // we do not accidentally activate Viewers that should not be on show again...
                const viewer = Window.viewer();
                if (viewer) {
                    viewer.state({name: "shown", value: shown});
                }

                // Only falsey when originating from this Viewer...
                if (!options.recursive) {
                    if (shown) {
                        model.state("active", model.get("_active"), options);
                    } else {
                        // Save previous active state so it can be restored - we do not want to activate embedded
                        // Experiences on adjacent Scenes, for example...
                        model.set("_active", model.state("active", false, options), {silent: true}); // "_active" is private
                    }
                }
            });

            // We need to deactivate any Viewer "as a whole" when the "active" property changes to ensure we do not
            // listen for "global events", e.g. resize or orientation changes, when this Viewer is inactive. This is
            // typically the case when this Viewer is embedded on some adjacent Scene currently not visible. A change
            // will only trigger a full re-render of the current scene, including overlay and background, as it will be
            // the only active Scene. This is initiated from  ViewerProxy.state({name: "active": value: true|false, ..})
            // and will from there recurse into relevant embedded children...
            this.listenTo(this.model, "change:active", (model, active) => {
                log.verbose(`${(active ? "Activated" : "Deactivated")} experience: ${model.get("experience")}`);

                // We sure all global window events are disabled once this viewer is inactive, including possible
                // references to parent/top windows. This is not the prettiest solution, but it ensures that other
                // types, like the BreakpointObserver type that knows nothing about the ExperienceViewModel from
                // within the inner most ExperienceController, will not continue to fire global events while
                // said Experience as a whole is hidden...
                Window.active(active);

                // Notify scenes -> widgets -> actions/embedded Experiences...
                this._getCurrentLayers().forEach((layer) => {
                    layer.setActive(active);
                });

                // Start playing again (unless still paused/stalled). We manually invoke the start/stop functions
                // as the model "paused" and "stalled" states are likely to have their old values from the time
                // this Viewer was made inactive, meaning a change event will not fire...
                if (active) {
                    this._startAutoPlay();

                // Stop playing...
                } else {
                    this._stopAutoPlay();
                }
            });

            // Regardless if embedded or direct url, if this Experience is stalled, we must do so for all reachable
            // Experiences, i.e. all recursively defined children of the top most Experience/Viewer (on {recursive: false})
            // *or* from this Experience/Viewer (on {recursive: true}), or they may continue to play while for example
            // a lightbox is opened or when navigation occurs away from a scene containing embedded Experiences. <p>
            //
            // This event can thus be triggered in one of three ways:
            //
            //   1. Internally from this Viewer, from 'experienceViewModel.set("stalled", true|false)'.
            //   2. During a top down recursive traversal of all reachable (embedded) Viewers, starting at the top
            //      most one reachable from this Viewer (for both embedded and direct urls). This is via
            //      ViewerProxy.state({stalled: .., ..}) and used to stall/pause all, for example on lightbox open.
            //   3. During a top down recursive traversal of all reachable (embedded) Viewers, starting at this Viewer
            //      (for both embedded and direct urls). This is via ViewerProxy.state({stalled: .., ..}) as well but
            //      used to stall/pause a sub-set of Experiences only, e.g. those reachable from a given Scene only.
            //
            this.listenTo(this.model, "change:stalled", (model, stalled, options) => {
                // Did the stalled event originate from this Viewer, or via recursion?
                const thisViewer = !options.recursive;

                // The "change:stalled" event is triggered internally (case 1), start the top down traversal from the
                // top most reachable Experience (case 2). However, during the top down traversal, we will never hit
                // this Viewer event again, since already stalled hence no change event, but the recursion will still
                // call "state(..)" on all embedded children. Also note that the top most Viewer may even be this Viewer.
                // This is why we fall-through to the pause logic below, knowing that a stall will typically be
                // triggered from an already paused Experience...
                if (thisViewer) {
                    // Start recursing from from the top? We cannot statically fetch the top most viewer as it might
                    // not even exist in the Editor, i.e. the top most Viewer viewed in preview mode uses different
                    // logic, and all embedded ones will actually on a different domain. Also, recursively embedding
                    // via Lightboxes should not (un)stall all those already stalled by other Lightboxes. This means
                    // that we cannot (un)pause such Experiences there. Top most viewer can only be null in the Editor
                    // or on hardcoded cross domain url by the client in the dom. However, the top most reachable
                    // viewer may not be the only top most Viewer as other siblings to it may exist, e.g. multiple
                    // top level Experiences on the same page. Also note that the top most viewer may be in the actual
                    // top level window on a direct url viewed directly as-is, but more likely a top most viewer will
                    // be in an iframe associated with the dom of the top level window (client site)...
                    const viewer = Window.viewer();
                    if (viewer) { // e.g. null in editor
                        // true: start from the closest intermediate root, since all other above it will have
                        // recursively been (un)stalled already...
                        const topMostViewers = viewer.root(true).siblings();
                        for (let i = 0; i < topMostViewers.length; i++) {
                            topMostViewers[i].state({name: "stalled", value: stalled});
                        }
                    }
                    // Fall-through to make sure *this* Viewer behaves as any of the other viewers except we make sure
                    // we unpause again on unstalling...
                }

                // On "options.recursive=true", it means we are triggered because of a top down traversal of all embedded
                // Viewers, starting from the top most reachable Experience or from some parent wanting to pause
                // all children on for example scene navigation (case 2 or 3, respectively). It is thus ok to trigger
                // a pause event now. This will also trigger pause of this Viewer itself when reached...
                // On stall, save the "paused" state so we do not unpause blindly when no longer stalled...
                const experience = model.get("experience");

                if (stalled) {
                    log.verbose(`Stalled experience: ${experience}`);
                    // If already paused, we do not need to do anything...
                    if (!model.get("paused")) {
                        // Since now stalled, this will trigger "_paused" to be computed whenever "paused" is
                        // changed while stalled. We add a "stalled: true" option is to ensure the computed property
                        // in ExperienceViewModel knows that it is this logic that does this...
                        options.stalled = true;
                        model.set("paused", true, {stalled: true, recursive: true}); // "model.set" options, not "proxy.state" options!

                    // Make sure we unpause on unstall this Viewer since the stall event originated at this Viewer. This
                    // is just by convention, but seem to fit our current use cases quite well...
                    } else if (thisViewer) {
                        model.set("_paused", true, {silent: true});
                    }

                // No longer stalled, so ensure we restore the "paused" state accordingly. The paused state can have
                // changed in the mean time, so the setting of it may not trigger a "changed:paused" event...
                } else {
                    log.verbose(`Unstalled experience: ${experience}`);
                    // The "_paused" attribute is "computed" based on "paused", but only while stalled and if it
                    // was originally set above. If present at all, it will be true initially when paused above, but
                    // it can be have flipped to false in the mean time. If so, somebody else explicitly unpaused
                    // while stalled and we will respect this here...
                    if (model.get("_paused")) { // "_paused" intentionally, not "paused"
                        // Since no longer stalled, this will trigger "_paused" to be set to undefined...
                        model.set("paused", false, {recursive: true}); // "model.set" options, not "proxy.state" options!
                    }
                }
            });

            // On first direct user interaction with a so configured action, ensure we stop auto play  - it can never
            // be enabled again for the specific Experience currently. That it, this does not recurse into
            // other Experiences/Viewers like "paused" and "stalled" potentially does...
            this.listenToOnce(this.model, "change:autoPlay", (model) => { // i.e. first time from true -> false
                log.verbose(`Disabled auto play for experience: ${model.get("experience")}`);
                this._stopAutoPlay();
            });

            // Regardless if embedded or direct url, if this Experience is paused/started, we must do so for all parent
            // Experiences recursively embedding this Experience. We do not care about sibling Experiences, e.g.
            // the parent Experience embedding other ones besides this one as well, since they should be allowed
            // to continue while the user is interacting with this one...
            this.listenTo(this.model, "change:paused", (model, paused, options) => { // options: model.set options, not proxy.state options
                const experience = model.get("experience");
                const autoPlay = model.get("autoPlay"); // even on unpause, auto play can still be disabled
                const parentState = Window.viewer("self", "parent.state"); // can be null, and may change over time, e.g. in the Editor

                // If this Experience is now paused, ensure that we stop auto play immediately...
                if (paused) {
                    log.verbose(`Paused auto play ${(autoPlay ? "enabled" : "disabled")} experience: ${experience}`);
                    this._stopAutoPlay();
                    // And do so recursively for all parents as well...
                    if (parentState && !options.recursive) { // "parentState" already recursively handled?
                        parentState({name: "paused", value: true, recursive: false});
                    }

                // If this Experience is no longer paused, start auto play again *unless* the user still hovers on this
                // Experience on next render. If so, the Experience should still be paused, and the auto play will not
                // start until "mouseleave" and we trigger yet another recursive "change:paused" event to ensure the
                // "paused" state is correct, including for all potential listeners...
                } else {
                    _.defer(() => {
                        if (this.$el.is(":hover")) {
                            model.set("paused", true); // will trigger this listener to be called again with paused = false
                        } else {
                            log.verbose(`Unpaused auto play ${(autoPlay ? "enabled" : "disabled")} experience: ${experience}`);
                            this._startAutoPlay();
                            // And do so recursively for all parents as well...
                            if (parentState && !options.recursive) { // "parentState" already recursively handled?
                                parentState({name: "paused", value: false, recursive: false});
                            }
                        }
                    });
                }
            });

            // Do we have an initial state to trigger on on the view model, passed in as options typically from a
            // parent viewer embedding this one...
            const state = this.options.state;
            if (state) {
                Window.active(state.value);
                // Silent because we are not ready to process the events yet, and relevant contexts will simply
                // initialise themselves bases on the model value at init time, and hence form on changes... :/
                this.model.set(state.name, state.value, {silent: true});
                delete this.options.state;
            }

            // Register for page show/hide events in order to disable scene transitions during inactivity. This is
            // when the user clicks a different browser tab, then comes back again sometime in the future. We only
            // need to do this for top level Viewers, i.e. those not embedded, or alternative all those that cannot
            // find a reachable parent (same thing), as they will recurse down to the embedded children...
            if ($.support.pageVisibility) {
                if (!Window.viewer("self", "parent")) {
                    $(window.document).on({ // only top level doc
                        "show.visibility": () => {
                            this.model.state("shown", true); // start from here, then recurse
                        },
                        "hide.visibility": () => {
                            this.model.state("shown", false); // start from here, then recurse
                        }
                    });
                }

            // Legacy behaviour, both in terms of functionality and browser. IE9 does not support the modern
            // page visibility api, and the jquery-visibility.js plugin tries to emulate it by focus events,
            // but that is semantically wrong, e.g. we get a "hide" event when the user clicks outside the
            // browser window while the page is still visible to the end user. We cannot trigger the "shown"
            // event as it will cascade into potentially disabling relevant stuff, making it unclickable until
            // the page regains focus, i.e. every second click is lost. This is nowhere near perfect, but probably
            // as good as it will get in IE 9...
            } else {
                const viewer = Window.viewer();
                if (viewer && !viewer.parent) {
                    $(window.document).on({ // only top level doc
                        "show.visibility": function() {
                            viewer.state({name: "paused", value: false, recursive: true}); // top-down recursive
                        },
                        "hide.visibility": function() {
                            viewer.state({name: "paused", value: true, recursive: true}); // top-down recursive
                        }
                    });
                }
            }

            // Support swiping to change scenes, depending on the transition
            const transitionType = this._transitionController.getTransitionType();

            if (transitionType.supportSwipes && this.numOfScenes > 1) {
                this.$el.swipe({
                    threshold: 40,
                    triggerOnTouchLeave: true,
                    allowPageScroll: transitionType.allowPageScroll || "auto",
                    swipe: (event, direction) => {
                        const experienceDirection = transitionType.swipeDirections[direction];

                        if (experienceDirection === ExperienceViewModel.DIRECTION_FORWARD) {
                            new SceneNavigationAction({target: SceneNavigationAction.NEXT}).activate(this.model);
                        } else if (experienceDirection === ExperienceViewModel.DIRECTION_REVERSE) {
                            new SceneNavigationAction({target: SceneNavigationAction.PREVIOUS}).activate(this.model);
                        }
                    }
                });
            }
        },

        // Throttle the current scene changed handler
        // Otherwise we see crashes on iOS when scene changes come too rapidly
        // The user will still end up on the correct scene, there just might be intermediate scenes that
        // aren't rendered
        _currentSceneChangeHandler(model) {
            // Since this is throttled, there might be a chance that *this* view is closed once this function
            // is invoked, make sure to bail out if that's the case...
            if (this.isClosed) {
                return;
            }

            // Stop auto play until the scene transition completes
            this._stopAutoPlay();

            const descriptor = this._updateToCurrentSceneNumber(model.previous("currentScene"));

            // Start the transition (and wait for it to complete)
            this._transitionController.transitionTo(descriptor.currentRegion.currentView).done((currentViewer, previousViewer) => {
                // Activate current scene, but based on the view model "active" state to ensure this Viewer has not
                // been deactivated since transition start...
                currentViewer.show().setActive(model.get("active"));
                // Previous viewer is no longer considered "active"
                if (previousViewer) {
                    previousViewer.setActive(false);
                }

                const disposableRegion = descriptor.disposableRegion;

                if (disposableRegion) {
                    // Dispose the disposable region
                    disposableRegion.close();
                    disposableRegion.$el.remove();
                }

                // Re-start the auto play routine (if we are active)...
                this._startAutoPlay();
            });
        },

        /**
         * @private
         *
         * Returns an array of scene numbers representing the current range of
         * scenes that needs to be rendered.
         *
         * @return {Array} an array of scene numbers representing the current range of
         * scenes that needs to be rendered. E.g. if the current scene number is 1, [8, 1, 2]
         * is returned (for an experience with 8 scenes) and <tt>NUM_REGIONS</tt> set to 3.
         */
        _getCurrentRange() {
            const currentSceneNumber = this.model.get("currentScene");
            const numberOfScenes = this.experience.getNumberOfScenes();
            const numRegions = this._sceneRegions.length;

            // If less than 3 regions, simply return [1] or [1, 2]
            if (numRegions < 3) {
                return _.range(1, numRegions + 1);
            }

            // Determine the range of scene numbers, e.g. [8, 1, 2] (for an experience with 8 scenes)
            const range = [];
            const numAdjacent = (numRegions - 1) / 2;
            let startSceneNumber = currentSceneNumber - numAdjacent;
            let i = 0;

            while (i++ < numRegions) {
                let sceneNumber = startSceneNumber;

                if (sceneNumber < 1) {
                    sceneNumber = numberOfScenes + startSceneNumber;
                } else if (sceneNumber > numberOfScenes) {
                    sceneNumber -= numberOfScenes;
                }

                range.push(sceneNumber);

                startSceneNumber++;
            }

            return range;
        },

        /**
         * @private
         *
         * Returns the region associated with the specified scene number, og <tt>undefined</tt>
         * if no region could be found.
         *
         * @param  {Number} sceneNumber The scene number.
         * @return {Marionette.Region}  the region associated with the specified scene number, og <tt>undefined</tt>
         * if no region could be found.
         */
        _getRegionFromSceneNumber(sceneNumber) {
            return _.find(this._sceneRegions, (sceneRegion) => sceneRegion.hasOwnProperty("sceneNumber") && sceneRegion.sceneNumber === sceneNumber);
        },

        /**
         * @private
         *
         * Manages the regions used for holding the <tt>SceneViewer</tt>'s.
         *
         * @param   {Number|Array} previousSceneNumber      The scene number of the previous scene or an <tt>Array</tt>
         *                                                  or scene numbers.
         * @param   {Array} range                           Optional range of scene numbers.
         * @param   {Map.<Number, Object>} sceneOptionsMap  Optional map of options for specific scenes.
         * @return  {Object}                                Convenience object containing a reference to the
         *                                                  current, previous and a disposable region (if any).
         *
         * @see #_currentSceneChangeHandler
         */
        _updateToCurrentSceneNumber(previousSceneNumber, range, sceneOptionsMap) {
            if (_.isArray(previousSceneNumber)) {
                range = previousSceneNumber;
                previousSceneNumber = undefined;
            }

            const sceneNumbers = range || this._getCurrentRange();
            const currentSceneNumber = this.model.get("currentScene");
            const previousRegion = this._getRegionFromSceneNumber(previousSceneNumber);

            let disposableRegion = null;

            // Determine regions that needs to be reset
            const resetRegions = _.filter(this._sceneRegions, (sceneRegion) => {
                const regionSceneNumber = sceneRegion.sceneNumber; /* We set a sceneNumber on the regions */
                return sceneNumbers.indexOf(regionSceneNumber) === -1;
            });

            const disposePrevious = resetRegions.indexOf(previousRegion) !== -1;

            // If we are to dispose the previous scene, e.g. the user navigated from scene 1 to scene 10
            if (disposePrevious) {
                // We still want the previous scene to transition out, so make sure the assocciated region for the scene is not re-used
                disposableRegion = previousRegion;
                // Remove if from the list of scene regions
                this._sceneRegions.splice(this._sceneRegions.indexOf(disposableRegion), 1);
                // Remove it from the list of regions that can be re-used
                resetRegions.splice(resetRegions.indexOf(disposableRegion), 1);
                // Add a new empty region to both lists
                const emptyRegion = this._addNewRegion();
                resetRegions.push(emptyRegion);
                this._sceneRegions.push(emptyRegion);
            }

            _.each(sceneNumbers, (sceneNumber) => {
                let region = this._getRegionFromSceneNumber(sceneNumber);
                const isCurrentScene = sceneNumber === currentSceneNumber;

                // Create new scenes if needed
                if (!region) {
                    region = resetRegions.pop();

                    const sceneOptions = sceneOptionsMap && sceneOptionsMap.has(sceneNumber) && sceneOptionsMap.get(sceneNumber);
                    const sceneViewer = this._createSceneViewer(sceneNumber, sceneOptions);

                    // Scenes are born "hidden" - the SceneTransitionController will manage visibility from here.
                    sceneViewer.hide();

                    region.sceneNumber = sceneNumber;
                    region.show(sceneViewer);
                }

                // Update region class name, e.g. "scene region 4 current"
                region.$el
                    .removeClass()
                    .addClass(`${CLASS_PREFIX} ${sceneNumber}${isCurrentScene ? " current" : ""}`);
            });

            return {
                currentRegion: this._getRegionFromSceneNumber(currentSceneNumber),
                previousRegion,
                disposableRegion
            };
        },

        _canStartPlay() {
            return  this.model.get("autoPlay") && // set to false on first direct user action
                    this.model.get("active")   && // can be toggled
                   !this.model.get("stalled")  && // can be toggled
                   !this.model.get("paused")   && // can be toggled
                    this.model.get("visible")  && // can be toggled
                    this.experience.getNumberOfScenes() > 1;
        },

        _startAutoPlay() {
            const autoPlayDelay = this.experience.get("autoPlayDelay");

            if (autoPlayDelay && this._autoPlayIntervalId === null && this._canStartPlay()) {
                this._autoPlayIntervalId = setInterval(this._autoPlayTickHandler.bind(this), autoPlayDelay);
            }
        },

        _stopAutoPlay() {
            if (this._autoPlayIntervalId !== null) {
                clearInterval(this._autoPlayIntervalId);
                this._autoPlayIntervalId = null;
            }
        },

        /**
         * @return {SceneViewer[]} A new array of the overlay, background, and current scene, in order; never falsey.
         */
        _getCurrentLayers() {
            return [
                this.overlayRegion.currentView,
                this.backgroundRegion.currentView,
                this._getCurrentSceneViewer()
            ];
        },

        /**
         * Invoked when the viewport visibility has changed.
         *
         * @param  {ExperienceViewModel} model The <tt>ExperienceViewModel.js</tt>.
         * @param  {Rectangle} viewport The visible viewport <tt>Rectangle</tt>.
         */
        _visibilityChangeHandler(/* model, viewport */) {
            this.shown().then(() => {
                this._getCurrentLayers().forEach((layer) => {
                    layer.updateToVisibilityState();
                });
                this._updateForVisibilityState();
            });
        },

        /**
         * Updates according to the <tt>viewportVisibility</tt> state, starting and stopping
         * auto play depending on whether the viewport is considered empty or not (part of the experience is visible).
         */
        _updateForVisibilityState() {
            const viewport = this.model.get("viewportVisibility");

            if (viewport.isEmpty()) {
                this._stopAutoPlay();
            } else {
                this._startAutoPlay();
            }
        },

        _autoPlayTickHandler() {
            // If there is only a single scene, the auto play just needs to replay the animations
            if (this.numOfScenes === 1) {
                this._getCurrentSceneViewer().startAnimations();
            } else {
                this.model.goToNextScene();
            }
        },

        _getCurrentSceneViewer() {
            const scene = this.model.get("currentScene");
            let region;

            if (scene) {
                region = this._getRegionFromSceneNumber(scene);
                if (region) {
                    return region.currentView;
                }
            }
            // undefined
        },

        _addNewRegion(sceneNumber) {
            sceneNumber = sceneNumber || NaN;

            const backgroundRegionElement = this.$(".background-region");
            const element = $("<div />")
                .addClass(CLASS_PREFIX)
                .insertAfter(backgroundRegionElement);

            const region = new Region({el: element});

            region.sceneNumber = sceneNumber;

            return region;
        },

        /**
         * @private
         *
         * Invoked when the experience model's scale changes.
         */
        _scaleChangeHandler() {
            this._updateSize();
        },

        /**
         * @private
         *
         * Sets the size of the experience element, equal to the scaled size of the current experience.
         */
        _updateSize() {
            this.$el.css({
                width: this.experience.get("scaledWidth"),
                height: this.experience.get("scaledHeight")
            });
        },

        /**
         * Sets up the needed regions and sets the initial size of the viewer.
         */
        onRender() {
            let i = 0;
            const numRegions = Math.min(this.experience.getNumberOfScenes(), NUM_REGIONS);

            // Create regions
            this._sceneRegions = [];

            while (i++ < numRegions) {
                this._sceneRegions.push(this._addNewRegion());
            }

            // Set the experience dimensions
            this.$el.css({
                width: this.experience.get("scaledWidth"),
                height: this.experience.get("scaledHeight")
            });
        },

        /**
         * Will show the experience, as soon as the responsive viewer controller is ready (we have a valid
         * view port size), failing otherwise.
         */
        onShow() {
            this._responsiveViewerController.start().done((valid) => {
                if (valid) {
                    this._doShow();
                }
            });
        },

        /**
         * @private
         * Creates the overlay and background scenes. Performs initial rendering of the current
         * scene and starts auto play if active.
         */
        _doShow() {
            const overlaySceneViewer = this._newSceneViewer({
                model:             this.experience.get("overlayScene"),
                viewModel:         this.model,
                extractCollection: false,
                enableAnimations:  true
            });

            const backgroundSceneViewer = this._newSceneViewer({
                model:             this.experience.get("backgroundScene"),
                viewModel:         this.model,
                extractCollection: false,
                enableAnimations:  true
            });

            const currentSceneOptions = new Map();
            currentSceneOptions.set(this.model.get("currentScene"), {extractCollection: false});

            let currentSceneViewer = this._updateToCurrentSceneNumber(null, null, currentSceneOptions).currentRegion.currentView;

            // Overlay and background should also stay transparent while rendering.
            overlaySceneViewer.$el.addClass("transparent");
            backgroundSceneViewer.$el.addClass("transparent");

            this.overlayRegion.show(overlaySceneViewer);
            this.backgroundRegion.show(backgroundSceneViewer);

            // Initial layout should only contain the current scene - once loaded, we'll add the adjacent scenes...
            InitialScenesController.process(this.model, overlaySceneViewer, backgroundSceneViewer, currentSceneViewer)
                .then(() => {
                    // For browsers that does not have proper support for CSS pointer events, we'll need
                    // to inject all the widgets contained in the overlay viewer into 'this' element, the overlay
                    // is static, so we can probably get away with this hack...
                    if (!Browser.capabilities.isPointerEventsSupported) {
                        this.$el.prepend(overlaySceneViewer.$el.children()); // TODO: test embedding experiences in IE?!
                        // Finally we hide the overlay region so it doesn't block pointer events
                        this.overlayRegion.$el.hide();
                    }
                });

            const descriptor = this._updateToCurrentSceneNumber();
            currentSceneViewer = descriptor.currentRegion.currentView; // scene number may have changed...

            // Register the initial scenes
            this._transitionController.registerInitialSceneViewers(currentSceneViewer, overlaySceneViewer, backgroundSceneViewer);

            // The view model "active" attribute is by default true. But, the view model may be primed with
            // "false" in case the Experience is shown on the next scene as an embedded Experience, not visible
            // yet. If we do not set it to inactive, we have conflicts between scene overlays. All widgets
            // will internally initially have their own active state set to false, and thus only the visible
            // Scene set to active here, or in the transition controller on navigation (but adhering to
            // the view model "active" property). Note the value can actually have changed since "_onShow"
            // was originally called...
            const active = this.model.get("active");

            // Show/(de-)activate all initial scenes...
            overlaySceneViewer.setActive(active);
            backgroundSceneViewer.setActive(active);
            currentSceneViewer.setActive(active);

            // Remove transparency (applied above) when fully rendered.
            overlaySceneViewer.$el.removeClass("transparent");
            backgroundSceneViewer.$el.removeClass("transparent");

            this._updateForVisibilityState();

            // Signal that the viewer is now fully rendered/shown
            this._shownDeferred.resolve(active);
        },

        /**
         * Creates the concrete <tt>SceneViewer</tt> instance.
         *
         * This may be overridden by subclasses.
         *
         * @param  {Object} options The default options passed to the <tt>SceneViewer</tt> constructor, see <tt>#_createSceneViewer</tt>.
         * @return {SceneViewer}    The concrete <tt>SceneViewer</tt> instance.
         */
        _newSceneViewer(options) {
            return new SceneViewer(options);
        },

        _createSceneViewer(sceneNumber, options) {
            const sceneOptions = _.extend({
                model:            this.experience.get("scenes").at(sceneNumber - 1),
                viewModel:        this.model,
                enableAnimations: true,
                disableScale:     true,
                sceneNumber
            }, options || {});

            return this._newSceneViewer(sceneOptions);
        },

        /**
         * @private
         * Ensures that all pending actions, such as a dangling opened Lightbox action, will be closed.
         */
        _closePendingActions() {
            const pendingActions = this.model.get("pendingActions");
            let i = pendingActions.length;

            while (i--) {
                pendingActions[i].close();
            }
        },

        onClose() {
            // Close pending actions, such as opened Lightboxes...
            this._closePendingActions();

            // Clear auto play timer if set...
            this._stopAutoPlay();

            // Close responsive viewer controller...
            this._responsiveViewerController.close();

            // Make sure we close down all (dynamically created) regions
            _.invoke(this._sceneRegions, "close");

            // Remove any 'visibility' listeners for browser tab changes...
            $(window.document).off(".visibility");

            log.verbose("Closed Viewer");
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/ExperienceViewer.js